/*
  Feiertage-Verwaltung für Shiftplan V2.

  Die Liste zeigt ein Kalenderjahr und holt es über einen eigenen, schlanken
  Aufruf – nur die Feiertage, nicht den Plan des ganzen Jahres. Nach einem
  Import, einem neuen oder gelöschten Feiertag steht sie sofort neu da; das
  Raster zieht im Hintergrund nach, ohne dass jemand darauf wartet. Früher
  hing jede Aktion am Neuladen des ganzen Plans, und die Liste blieb danach
  trotzdem auf dem alten Stand.

  Rückmeldung kommt als Toast der Seite (`onFeedback`): während des Imports
  ein Ladehinweis – die Feiertage kommen von einem externen Dienst und das
  kann dauern –, danach Erfolg oder Fehler.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { IconAdd, IconChevronDown, IconDelete } from "../../../../design-system/icons";
import ShiftplanBusyDots from "../ui/ShiftplanBusyDots";
import ShiftplanTooltip from "../tooltips/ShiftplanTooltip";
import {
  deletePublicHolidays,
  getPublicHolidayLocationSuggestions,
  getPublicHolidays,
  previewPublicHolidays,
  savePublicHolidayBatch,
} from "../../api/shiftplanApi";
import { parseDateValue, toIsoDate } from "../../domain/date/month";

const COUNTRY_OPTIONS = Object.freeze([
  { value: "CH", label: "Schweiz" },
  { value: "DE", label: "Deutschland" },
  { value: "AT", label: "Österreich" },
  { value: "FR", label: "Frankreich" },
  { value: "IT", label: "Italien" },
  { value: "LI", label: "Liechtenstein" },
]);

const LIST_STATUS = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
  INVALID_YEAR: "invalid-year",
});

// Nur echte Jahre laden – beim Tippen von "2027" nicht auch "2", "20" und "202".
const MIN_YEAR = 1900;
const MAX_YEAR = 2200;
const ERROR_TOAST_DURATION = 4200;

function getYearFromDate(value) {
  const parsed = parseDateValue(value) || new Date();
  return parsed.getFullYear();
}

function normalizeText(value) {
  return String(value || "").trim();
}

function formatDate(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return String(value || "");

  return new Intl.DateTimeFormat("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
}

function getSourceType(row) {
  return normalizeText(row?.sourceType || row?.source_type) || "manual";
}

function getCountryCode(row) {
  return normalizeText(row?.countryCode || row?.country_code);
}

function getSubdivisionCode(row) {
  return normalizeText(row?.subdivisionCode || row?.subdivision_code);
}

function getLocationLabel(row) {
  return normalizeText(row?.locationLabel || row?.location_label);
}

function getExternalId(row) {
  return normalizeText(row?.externalId || row?.external_id);
}

function getLocalName(row) {
  return normalizeText(row?.localName || row?.local_name);
}

function normalizeHolidayInput(row) {
  const date = toIsoDate(row?.date || row?.holiday_date);
  const name = normalizeText(row?.name);
  if (!date || !name) return null;

  return {
    date,
    name,
    local_name: getLocalName(row) || name,
    source_type: getSourceType(row),
    location_label: getLocationLabel(row) || null,
    country_code: getCountryCode(row) || null,
    subdivision_code: getSubdivisionCode(row) || null,
    external_id: getExternalId(row) || null,
  };
}

function toApiHoliday(row) {
  return {
    date: row.date,
    name: row.name,
    local_name: row.local_name || row.name,
    source_type: row.source_type || "manual",
    location_label: row.location_label || null,
    country_code: row.country_code || null,
    subdivision_code: row.subdivision_code || null,
    external_id: row.external_id || null,
  };
}

function sourceLabel(row) {
  return getSourceType(row) === "fetched" ? "Importiert" : "Manuell";
}

function holidayMeta(row) {
  return [sourceLabel(row), getCountryCode(row), getLocationLabel(row)].filter(Boolean).join(" · ");
}

function sortHolidayRows(left, right) {
  const dateCompare = String(left.date || "").localeCompare(String(right.date || ""));
  if (dateCompare !== 0) return dateCompare;
  return String(left.name || "").localeCompare(String(right.name || ""), "de");
}

function normalizeHolidayRows(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeHolidayInput).filter(Boolean).sort(sortHolidayRows);
}

// Die gespeicherten Feiertage eines Jahres, wie die Liste sie zeigt.
function normalizeSavedHolidays(rows, year) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => Number(String(row?.date || "").slice(0, 4)) === Number(year))
    .sort(sortHolidayRows);
}

function isListableYear(value) {
  const year = Number(value);
  return Number.isInteger(year) && year >= MIN_YEAR && year <= MAX_YEAR;
}

function formatHolidayCount(count) {
  return count === 1 ? "1 Feiertag" : `${count} Feiertage`;
}

function suggestionKey(suggestion) {
  return [
    suggestion?.country_code,
    suggestion?.postal_code,
    suggestion?.city,
    suggestion?.subdivision_code,
  ].filter(Boolean).join("|");
}

function suggestionLabel(suggestion) {
  return [suggestion?.location_label, suggestion?.region, suggestion?.subdivision_code].filter(Boolean).join(" · ") || "Gemeinde";
}

function HolidayList({ rows, emptyText, busyAction, isBusy, onDelete }) {
  if (!rows.length) {
    return (
      <Box className="spv2-holidays-empty">
        <Typography className="spv2-holidays-empty-title">{emptyText}</Typography>
      </Box>
    );
  }

  return (
    <Box className="spv2-holidays-list">
      {rows.map((row) => {
        const isDeleting = busyAction === `delete:${Number(row.id)}`;

        return (
          <Box
            key={row.id || `${row.date}:${row.name}:${getCountryCode(row)}`}
            className={`spv2-holidays-row${isDeleting ? " is-busy" : ""}`}
            aria-busy={isDeleting || undefined}
          >
            <Box className="spv2-holidays-date">
              <Typography className="spv2-holidays-date-day">{formatDate(row.date).slice(0, 5)}</Typography>
              <Typography className="spv2-holidays-date-year">{String(row.date || "").slice(0, 4)}</Typography>
            </Box>
            <Box className="spv2-holidays-row-copy">
              <Typography className="spv2-holidays-row-title">{row.name}</Typography>
              <Typography className="spv2-holidays-row-meta">{holidayMeta(row)}</Typography>
            </Box>
            <ShiftplanTooltip title="Feiertag löschen">
              <span>
                <IconButton
                  className="spv2-holidays-delete-btn"
                  size="small"
                  onClick={() => onDelete(row)}
                  disabled={isBusy}
                  aria-label={`${row.name} löschen`}
                >
                  {isDeleting ? <ShiftplanBusyDots label="Feiertag wird gelöscht" /> : <IconDelete fontSize="small" />}
                </IconButton>
              </span>
            </ShiftplanTooltip>
          </Box>
        );
      })}
    </Box>
  );
}

function HolidayListState({ status, year, onRetry }) {
  if (status === LIST_STATUS.INVALID_YEAR) {
    return (
      <Box className="spv2-holidays-empty spv2-holidays-list-state">
        <Typography className="spv2-holidays-empty-title">Gib ein Jahr ein, z. B. {new Date().getFullYear()}</Typography>
      </Box>
    );
  }

  if (status === LIST_STATUS.ERROR) {
    return (
      <Box className="spv2-holidays-empty spv2-holidays-list-state">
        <Typography className="spv2-holidays-empty-title">Die Feiertage konnten nicht geladen werden.</Typography>
        <Button size="small" className="spv2-holidays-retry-btn" onClick={onRetry}>
          Nochmal versuchen
        </Button>
      </Box>
    );
  }

  return (
    <Box className="spv2-holidays-empty spv2-holidays-list-state" role="status">
      <ShiftplanBusyDots label={`Feiertage ${year} werden geladen`} />
      <Typography className="spv2-holidays-empty-title">Feiertage werden geladen</Typography>
    </Box>
  );
}

export function PublicHolidaysContent({ active = true, referenceDate, onChanged, onFeedback }) {
  const initialYear = useMemo(() => getYearFromDate(referenceDate), [referenceDate]);
  const [year, setYear] = useState(initialYear);
  const [countryCode, setCountryCode] = useState("CH");
  const [postalCode, setPostalCode] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [selectedLocationKey, setSelectedLocationKey] = useState("");
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [manualDate, setManualDate] = useState("");
  const [manualName, setManualName] = useState("");
  const [busyAction, setBusyAction] = useState("");
  // null, solange das gewählte Jahr noch nicht geladen ist.
  const [holidays, setHolidays] = useState(null);
  const [listStatus, setListStatus] = useState(LIST_STATUS.LOADING);
  const locationRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const isBusy = Boolean(busyAction);
  const listYear = Number(year);
  const canListYear = isListableYear(year);

  useEffect(() => {
    if (!active) return;
    setYear(initialYear);
    setCountryCode("CH");
    setPostalCode("");
    setLocationSuggestions([]);
    setSelectedLocationKey("");
    setIsResolvingLocation(false);
    setManualDate("");
    setManualName("");
    setBusyAction("");
  }, [active, initialYear]);

  /*
    `quiet` lädt nach einer Änderung neu, ohne die Liste zwischendurch durch
    den Ladehinweis zu ersetzen – sie springt dann direkt auf den neuen Stand.
    Überholte Antworten (schnell gewechseltes Jahr) werden verworfen.
  */
  const loadHolidays = useCallback(async (targetYear, { quiet = false } = {}) => {
    const requestId = (listRequestRef.current += 1);
    if (!quiet) {
      setHolidays(null);
      setListStatus(LIST_STATUS.LOADING);
    }

    try {
      const response = await getPublicHolidays(targetYear);
      if (requestId !== listRequestRef.current) return;
      setHolidays(normalizeSavedHolidays(response?.public_holidays, targetYear));
      setListStatus(LIST_STATUS.READY);
    } catch (_error) {
      if (requestId !== listRequestRef.current) return;
      setListStatus(LIST_STATUS.ERROR);
    }
  }, []);

  useEffect(() => {
    if (!active || !canListYear) return;
    loadHolidays(listYear);
  }, [active, canListYear, listYear, loadHolidays]);

  const selectedLocation = useMemo(
    () => locationSuggestions.find((suggestion) => suggestionKey(suggestion) === selectedLocationKey) || null,
    [locationSuggestions, selectedLocationKey]
  );

  useEffect(() => {
    const normalizedPostalCode = postalCode.trim();
    setSelectedLocationKey("");

    if (!active || normalizedPostalCode.length < 3 || !countryCode) {
      setLocationSuggestions([]);
      setIsResolvingLocation(false);
      return undefined;
    }

    const requestId = locationRequestRef.current + 1;
    locationRequestRef.current = requestId;
    setIsResolvingLocation(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await getPublicHolidayLocationSuggestions({
          countryCode,
          postalCode: normalizedPostalCode,
        });
        if (locationRequestRef.current !== requestId) return;

        const suggestions = Array.isArray(response?.location_suggestions) ? response.location_suggestions : [];
        setLocationSuggestions(suggestions);
        setSelectedLocationKey(suggestions[0] ? suggestionKey(suggestions[0]) : "");
      } catch (_error) {
        if (locationRequestRef.current !== requestId) return;
        setLocationSuggestions([]);
      } finally {
        if (locationRequestRef.current === requestId) {
          setIsResolvingLocation(false);
        }
      }
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [active, countryCode, postalCode]);

  const notify = (message, type = "success", options) => onFeedback?.(message, type, options);
  const notifyError = (message) => notify(message, "error", { duration: ERROR_TOAST_DURATION });

  // Das Raster zieht im Hintergrund nach – niemand soll auf den ganzen Plan warten.
  const refreshPlanInBackground = () => {
    Promise.resolve(onChanged?.()).catch(() => {});
  };

  const handleImportOfficialHolidays = async () => {
    if (isBusy || !canListYear || !countryCode) return;

    const importYear = listYear;
    setBusyAction("import");
    notify(`Feiertage ${importYear} werden importiert …`, "loading");
    try {
      const response = await previewPublicHolidays({
        year: importYear,
        countryCode,
        postalCode,
        city: selectedLocation?.city,
        subdivisionCode: selectedLocation?.subdivision_code,
        locationLabel: selectedLocation?.location_label,
      });
      const holidayRows = normalizeHolidayRows(response?.public_holidays);

      if (!holidayRows.length) {
        notify(`Für ${importYear} wurden keine offiziellen Feiertage gefunden.`, "info", {
          duration: ERROR_TOAST_DURATION,
        });
        return;
      }

      await savePublicHolidayBatch({
        items: holidayRows.map(toApiHoliday),
        fetchedScope: {
          year: importYear,
          country_code: response?.resolved_location?.country_code || countryCode,
          subdivision_code: response?.resolved_location?.subdivision_code || null,
        },
      });
      await loadHolidays(importYear, { quiet: true });
      notify(`${formatHolidayCount(holidayRows.length)} für ${importYear} importiert`);
      refreshPlanInBackground();
    } catch (_error) {
      notifyError("Die Feiertage konnten nicht importiert werden. Versuch es bitte nochmal.");
    } finally {
      setBusyAction("");
    }
  };

  const handleCreateManualHoliday = async () => {
    const manualHoliday = normalizeHolidayInput({
      date: manualDate,
      name: manualName,
      source_type: "manual",
      country_code: countryCode,
    });
    if (!manualHoliday || isBusy) return;

    const holidayYear = getYearFromDate(manualHoliday.date);
    setBusyAction("manual");
    try {
      await savePublicHolidayBatch({
        items: [toApiHoliday(manualHoliday)],
        fetchedScope: null,
      });
      setManualDate("");
      setManualName("");
      if (holidayYear === listYear) {
        await loadHolidays(holidayYear, { quiet: true });
      } else {
        // Der Feiertag liegt in einem anderen Jahr – die Liste springt dorthin.
        setYear(holidayYear);
      }
      notify(`Feiertag „${manualHoliday.name}“ hinzugefügt`);
      refreshPlanInBackground();
    } catch (_error) {
      notifyError("Der Feiertag konnte nicht gespeichert werden. Versuch es bitte nochmal.");
    } finally {
      setBusyAction("");
    }
  };

  const handleDeleteSaved = async (row) => {
    const id = Number(row?.id);
    if (!Number.isFinite(id) || isBusy) return;

    setBusyAction(`delete:${id}`);
    try {
      await deletePublicHolidays([id]);
      setHolidays((current) => (current || []).filter((item) => Number(item.id) !== id));
      notify(`Feiertag „${row.name}“ gelöscht`);
      refreshPlanInBackground();
    } catch (_error) {
      notifyError("Der Feiertag konnte nicht gelöscht werden. Versuch es bitte nochmal.");
    } finally {
      setBusyAction("");
    }
  };

  const isImporting = busyAction === "import";
  const isAddingManual = busyAction === "manual";
  const showListState = !canListYear || !holidays || listStatus === LIST_STATUS.ERROR;
  const shownListStatus = canListYear ? listStatus : LIST_STATUS.INVALID_YEAR;

  return (
    <Box className="spv2-holidays-panel">
      <Box className="spv2-settings-section-head">
        <Box className="spv2-settings-section-copy">
          <Typography className="spv2-settings-section-title">Feiertage</Typography>
        </Box>
        <TextField
          className="spv2-holidays-field spv2-holidays-year-field"
          label="Jahr"
          type="number"
          size="small"
          value={year}
          disabled={isBusy}
          onChange={(event) => setYear(event.target.value)}
        />
      </Box>

        <Box className="spv2-holidays-actions-grid">
          <Box className="spv2-holidays-card">
            <Box className="spv2-holidays-card-head">
              <Box>
                <Typography className="spv2-holidays-card-title">Offizielle Feiertage</Typography>
              </Box>
            </Box>
            <Box className="spv2-holidays-import-grid">
              <TextField
                className="spv2-holidays-field"
                label="Land"
                select
                size="small"
                value={countryCode}
                disabled={isImporting}
                onChange={(event) => setCountryCode(event.target.value)}
                SelectProps={{
                  IconComponent: IconChevronDown,
                  MenuProps: {
                    // Über den Einstellungen-Dialog (3300), wie die anderen
                    // Menüs und Popover der Seite.
                    sx: { zIndex: 3400 },
                    PaperProps: { className: "spv2-holidays-country-menu" },
                  },
                }}
              >
                {COUNTRY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                className="spv2-holidays-field"
                label="PLZ"
                size="small"
                value={postalCode}
                disabled={isImporting}
                onChange={(event) => setPostalCode(event.target.value)}
              />
            </Box>
            {postalCode.trim().length >= 3 ? (
              <Box className="spv2-holidays-location-suggestions">
                {isResolvingLocation ? (
                  <Box className="spv2-holidays-location-state">
                    <ShiftplanBusyDots label="Gemeinde wird gesucht" />
                    <span>Gemeinde wird gesucht</span>
                  </Box>
                ) : null}
                {!isResolvingLocation && locationSuggestions.length ? (
                  locationSuggestions.map((suggestion) => {
                    const key = suggestionKey(suggestion);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`spv2-holidays-location-option${selectedLocationKey === key ? " is-selected" : ""}`}
                        onClick={() => setSelectedLocationKey(key)}
                        disabled={isImporting}
                      >
                        {suggestionLabel(suggestion)}
                      </button>
                    );
                  })
                ) : null}
                {!isResolvingLocation && !locationSuggestions.length ? (
                  <Box className="spv2-holidays-location-state">Keine Gemeinde gefunden</Box>
                ) : null}
              </Box>
            ) : null}
            <Button
              className="spv2-holidays-action-btn"
              startIcon={
                isImporting ? (
                  <ShiftplanBusyDots label="Feiertage werden importiert" />
                ) : (
                  <CloudDownloadOutlinedIcon fontSize="small" />
                )
              }
              onClick={handleImportOfficialHolidays}
              disabled={isBusy || !canListYear || !countryCode}
              aria-busy={isImporting || undefined}
            >
              {isImporting ? "Wird importiert …" : "Feiertage importieren"}
            </Button>
          </Box>

          <Box className="spv2-holidays-card">
            <Box className="spv2-holidays-card-head">
              <Box>
                <Typography className="spv2-holidays-card-title">Eigener Feiertag</Typography>
              </Box>
            </Box>
            <Box className="spv2-holidays-manual-grid">
              <TextField
                className="spv2-holidays-field"
                label="Datum"
                type="date"
                size="small"
                value={manualDate}
                disabled={isAddingManual}
                onChange={(event) => setManualDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                className="spv2-holidays-field"
                label="Bezeichnung"
                size="small"
                value={manualName}
                disabled={isAddingManual}
                onChange={(event) => setManualName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleCreateManualHoliday();
                }}
              />
            </Box>
            <Button
              className="spv2-holidays-action-btn"
              startIcon={
                isAddingManual ? <ShiftplanBusyDots label="Feiertag wird hinzugefügt" /> : <IconAdd fontSize="small" />
              }
              onClick={handleCreateManualHoliday}
              disabled={isBusy || !manualDate || !manualName.trim()}
              aria-busy={isAddingManual || undefined}
            >
              {isAddingManual ? "Wird hinzugefügt …" : "Feiertag hinzufügen"}
            </Button>
          </Box>
        </Box>

        <Box className="spv2-holidays-list-card spv2-holidays-list-card--full">
          <Box className="spv2-holidays-list-head">
            <Typography className="spv2-holidays-list-title">Gespeicherte Feiertage</Typography>
            <Typography className="spv2-holidays-count">{holidays && canListYear ? holidays.length : "–"}</Typography>
          </Box>
          {showListState ? (
            <HolidayListState status={shownListStatus} year={year} onRetry={() => loadHolidays(listYear)} />
          ) : (
            <HolidayList
              rows={holidays}
              emptyText={`Keine Feiertage für ${year} vorhanden`}
              busyAction={busyAction}
              isBusy={isBusy}
              onDelete={handleDeleteSaved}
            />
          )}
        </Box>
      </Box>
  );
}
